import { Spinner } from '@consigli/facade';
import {
  useGetLayoutOptimizationQuery,
  useLayoutId,
  useProject,
  useProjectId,
} from '@consigli/hooks';
import { JobType } from '@consigli/types';

import { useRegisterBreadcrumb } from '@/components';
import { PageHeader } from '@/components/page-header';
import { Tabs } from '@/components/tabs';
import { ServiceType } from '@/utils/types';
import { useTabParam } from '@/utils/use-tab-param';

import { DocumentsTab } from './documents/documents-tab';
import { UploadLayoutDocumentForm } from './documents/form/upload-layout-document-form';
import { CreateFindingForm } from './findings/create-finding-form/create-finding-form';
import { PipelinesTab } from './pipelines/pipelines-tab';
import { StartPipelineMenu } from './pipelines/start-pipeline-menu';
import { ResultsTab, UploadResultForm } from './results';

const TAB_VALUES = ['documents', 'results', 'findings', 'pipelines'] as const;
type TabValue = (typeof TAB_VALUES)[number];

export const LayoutOptimizationPage = () => {
  const layoutOptimizationId = useLayoutId();
  const projectId = useProjectId();
  const [selectedTab, setSelectedTab] = useTabParam<TabValue>('documents', TAB_VALUES);
  const { project } = useProject(projectId);
  const { data, isLoading } = useGetLayoutOptimizationQuery({
    projectId,
    layoutId: layoutOptimizationId,
  });

  useRegisterBreadcrumb(projectId, project?.name);
  useRegisterBreadcrumb(layoutOptimizationId, data?.name);

  if (isLoading) return <Spinner />;

  if (!data) return <div>Layout optimization not found</div>;

  return (
    <div>
      <PageHeader title="Layout Optimization" subtitle={data.name}>
        {selectedTab === 'documents' && <UploadLayoutDocumentForm />}
        {selectedTab === 'results' && <UploadResultForm />}
        {selectedTab === 'findings' && (
          <CreateFindingForm projectId={projectId} packageId={layoutOptimizationId} />
        )}
        {selectedTab === 'pipelines' && (
          <StartPipelineMenu
            projectId={projectId}
            packageId={layoutOptimizationId}
            jobType={JobType.LAYOUT_OPTIMIZATION}
          />
        )}
      </PageHeader>
      <Tabs
        value={selectedTab}
        onValueChange={(value) => setSelectedTab(value as TabValue)}
        tabs={[
          {
            value: 'documents',
            label: 'Documents',
            content: (
              <DocumentsTab
                serviceId={layoutOptimizationId}
                projectId={projectId}
                serviceType={ServiceType.LAYOUT_OPTIMIZATION}
              />
            ),
          },
          {
            value: 'results',
            label: 'Results',
            content: <ResultsTab />,
          },
          // {
          //   value: 'findings',
          //   label: 'Findings',
          //   content: <FindingsTab documentPackage={data} />,
          // },
          {
            value: 'pipelines',
            label: 'Pipelines',
            content: <PipelinesTab projectId={projectId} packageId={layoutOptimizationId} />,
          },
        ]}
      />
    </div>
  );
};
